import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { asMoney } from "@bw/shared/types";
import {
  useProgramPreviewIdentity,
  useProgramPreviewPage,
  useProgramPreviewQuery,
} from "@bw/shared/hooks";

import { listCourts } from "@/libs/facilityApi";
import {
  cancelProgram,
  createTrack,
  deleteTrack,
  getProgram,
  getTrack,
  listTracks,
  publishProgram,
  type ProgramTrackCreate,
  type ProgramTrackOptionResponse,
  type UnifiedProgramResponse,
  type TrackFeeType,
  type TrackSkillLevel,
} from "@/libs/programsApi";
import { formatDate } from "./program-detail-time";
import { RosterSection } from "./program-roster-section";
import { ProgramLearningSection } from "./program-learning-section";
import { SessionsSection, type CourtOption } from "./program-sessions-section";
import { ProgramInstructorCard } from "./program-instructor-card";

const FEE_TYPE_OPTIONS = [
  "per_session",
  "per_series",
  "per_month",
  "per_package",
] as const satisfies readonly TrackFeeType[];
const SKILL_LEVEL_OPTIONS = [
  "all",
  "beginner",
  "intermediate",
  "advanced",
] as const satisfies readonly TrackSkillLevel[];

const PROGRAM_STATUS_COLOR: Record<
  string,
  "default" | "error" | "info" | "success" | "warning"
> = {
  draft: "warning",
  published: "success",
  cancelled: "error",
  completed: "default",
};
const TRACK_STATUS_COLOR: Record<
  string,
  "default" | "error" | "success" | "warning"
> = {
  active: "success",
  closed: "warning",
  cancelled: "error",
};

type TrackFormValues = {
  ageMax: string;
  ageMin: string;
  currency: string;
  defaultCourtIds: string[];
  dropInPrice: string;
  enrollmentModes: string[];
  feeType: TrackFeeType;
  lateJoinCutoffHours: string;
  maxStudents: string;
  minStudents: string;
  name: string;
  price: string;
  skillLevel: TrackSkillLevel;
};

const emptyTrack: TrackFormValues = {
  ageMax: "",
  ageMin: "",
  currency: "USD",
  defaultCourtIds: [],
  dropInPrice: "",
  enrollmentModes: ["full_course"],
  feeType: "per_series",
  lateJoinCutoffHours: "0",
  maxStudents: "1",
  minStudents: "1",
  name: "",
  price: "",
  skillLevel: "all",
};

const trackSchema = z
  .object({
    ageMax: z.string(),
    ageMin: z.string(),
    currency: z.string(),
    defaultCourtIds: z.array(z.string()),
    dropInPrice: z.string(),
    enrollmentModes: z.array(z.string()).min(1, "Choose an enrollment option."),
    feeType: z.enum(FEE_TYPE_OPTIONS),
    lateJoinCutoffHours: z.string(),
    maxStudents: z.string(),
    minStudents: z.string(),
    name: z.string().refine((value) => value.trim().length > 0, {
      message: "Name is required.",
    }),
    price: z.string().refine(
      (value) => {
        const amount = Number(value);
        return value !== "" && Number.isFinite(amount) && amount >= 0;
      },
      { message: "Enter a fee of 0 or more." },
    ),
    skillLevel: z.enum(SKILL_LEVEL_OPTIONS),
  })
  .superRefine((values, context) => {
    const maximum = Number(values.maxStudents);
    const minimum = Number(values.minStudents);
    if (!Number.isFinite(maximum) || maximum < 1) {
      context.addIssue({
        code: "custom",
        message: "Max students must be at least 1.",
        path: ["maxStudents"],
      });
    } else if (Number.isFinite(minimum) && maximum < minimum) {
      context.addIssue({
        code: "custom",
        message: "Max students must be ≥ min students.",
        path: ["maxStudents"],
      });
    }
    if (values.enrollmentModes.includes("session_drop_in")) {
      const amount = Number(values.dropInPrice);
      if (values.dropInPrice === "" || !Number.isFinite(amount) || amount < 0) {
        context.addIssue({
          code: "custom",
          message: "Enter a drop-in price of 0 or more.",
          path: ["dropInPrice"],
        });
      }
    }
  });

const cancelProgramSchema = z.object({ reason: z.string() });
type CancelProgramValues = z.infer<typeof cancelProgramSchema>;

const fmtLabel = (value: string | null | undefined) =>
  value
    ? `${value.replace(/_/g, " ").charAt(0).toUpperCase()}${value
        .replace(/_/g, " ")
        .slice(1)}`
    : "—";

function formatMoney(
  value: number | string | null | undefined,
  currency: string,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return String(value);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  } catch {
    return `${currency || "USD"} ${value}`;
  }
}

function formatAgeRange(
  minimum: number | null | undefined,
  maximum: number | null | undefined,
): string {
  if (
    (minimum === null || minimum === undefined) &&
    (maximum === null || maximum === undefined)
  ) {
    return "All ages";
  }
  if (
    minimum !== null &&
    minimum !== undefined &&
    maximum !== null &&
    maximum !== undefined
  ) {
    return `${minimum}–${maximum}`;
  }
  if (minimum !== null && minimum !== undefined) return `${minimum}+`;
  return `Up to ${maximum}`;
}

function FactRow({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Box
      sx={{
        gridColumn: wide ? "1 / -1" : undefined,
        minWidth: 0,
      }}
    >
      <Typography color="text.secondary" component="div" variant="overline">
        {label}
      </Typography>
      <Typography sx={{ overflowWrap: "anywhere" }} variant="body2">
        {children}
      </Typography>
    </Box>
  );
}

function ProgramTitle({ program }: { program: UnifiedProgramResponse }) {
  const privateCoaching = program.audience === "private_coaching";
  return <Box>
    <Stack direction="row" spacing={1.5} useFlexGap
      sx={{ alignItems: "center", flexWrap: "wrap" }}>
      <Typography component="h1" variant="h4" sx={{ overflowWrap: "anywhere" }}>
        {program.name}
      </Typography>
      {privateCoaching ? (
        <Chip label="Private coaching" size="small" color="info" variant="outlined" />
      ) : null}
      <Chip color={PROGRAM_STATUS_COLOR[program.status] ?? "default"}
        label={fmtLabel(program.status)} size="small" variant="outlined" />
    </Stack>
    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
      {privateCoaching ? "Business-owned private delivery" : fmtLabel(program.program_type)}
    </Typography>
  </Box>;
}

function AddTrackDialog({
  open,
  onClose,
  onSubmit,
  courts,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProgramTrackCreate) => Promise<void>;
  courts: CourtOption[];
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<TrackFormValues>({
    defaultValues: emptyTrack,
    resolver: zodResolver(trackSchema),
  });
  const selectedCourtIds = watch("defaultCourtIds");
  const selectedEnrollmentModes = watch("enrollmentModes");

  useEffect(() => {
    if (!open) return;
    reset(emptyTrack);
    setSubmitError(null);
  }, [open, reset]);

  const submit = handleSubmit(async (values) => {
    const wire: ProgramTrackCreate = {
      name: values.name,
      price: asMoney(values.price) ?? asMoney("0")!,
      currency: values.currency,
      fee_type: values.feeType,
      min_students: Number(values.minStudents),
      max_students: Number(values.maxStudents),
      age_min: values.ageMin === "" ? null : Number(values.ageMin),
      age_max: values.ageMax === "" ? null : Number(values.ageMax),
      skill_level: values.skillLevel,
      default_court_ids: values.defaultCourtIds,
      enrollment_modes:
        values.enrollmentModes as ProgramTrackCreate["enrollment_modes"],
      late_join_cutoff_hours: Number(values.lateJoinCutoffHours || "0"),
      ...(values.enrollmentModes.includes("session_drop_in")
        ? { drop_in_price: asMoney(values.dropInPrice) ?? asMoney("0")! }
        : {}),
    };
    setSubmitError(null);
    try {
      await onSubmit(wire);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Failed to add track.",
      );
    }
  });

  return (
    <Dialog
      aria-labelledby="add-track-title"
      fullWidth
      maxWidth="sm"
      onClose={isSubmitting ? undefined : onClose}
      open={open}
    >
      <Box component="form" onSubmit={submit}>
        <DialogTitle id="add-track-title">Add track</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            A track is a fee and capacity variant within this program, such as
            an age or skill group.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              error={Boolean(errors.name)}
              fullWidth
              helperText={errors.name?.message}
              id="track-name"
              label="Name"
              placeholder="e.g. Youth Beginner"
              {...register("name")}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                error={Boolean(errors.price)}
                fullWidth
                helperText={errors.price?.message}
                id="track-fee"
                label="Fee per student"
                placeholder="0.00"
                slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
                type="number"
                {...register("price")}
              />
              <Controller
                control={control}
                name="currency"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="track-currency"
                    label="Currency"
                    onChange={(event) =>
                      field.onChange(event.target.value.toUpperCase())
                    }
                    slotProps={{ htmlInput: { maxLength: 3 } }}
                  />
                )}
              />
            </Stack>
            <Controller
              control={control}
              name="feeType"
              render={({ field }) => (
                <TextField {...field} fullWidth label="Fee type" select>
                  {FEE_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {fmtLabel(option)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <FormControl component="fieldset">
              <Typography component="legend" variant="subtitle2">
                Enrollment options
              </Typography>
              <FormGroup>
                {[
                  ["full_course", "Full course — all sessions"],
                  [
                    "remaining_course",
                    "Join late — prorated remaining sessions",
                  ],
                  [
                    "session_drop_in",
                    "Single-session drop-in — no Learning Space",
                  ],
                ].map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        checked={selectedEnrollmentModes.includes(value)}
                        onChange={() => {
                          const next = selectedEnrollmentModes.includes(value)
                            ? selectedEnrollmentModes.filter(
                                (mode) => mode !== value,
                              )
                            : [...selectedEnrollmentModes, value];
                          setValue("enrollmentModes", next, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
              <FormHelperText>
                Learning Space access is included only when enabled for this
                course. Drop-ins do not include access.
              </FormHelperText>
              {errors.enrollmentModes ? (
                <FormHelperText error>
                  {errors.enrollmentModes.message}
                </FormHelperText>
              ) : null}
            </FormControl>
            {selectedEnrollmentModes.includes("remaining_course") ? (
              <TextField
                id="track-late-cutoff"
                label="Late-join cutoff (hours before session)"
                type="number"
                slotProps={{ htmlInput: { min: 0, max: 720 } }}
                {...register("lateJoinCutoffHours")}
              />
            ) : null}
            {selectedEnrollmentModes.includes("session_drop_in") ? (
              <TextField
                id="track-drop-in-price"
                label="Single-session drop-in price"
                type="number"
                error={Boolean(errors.dropInPrice)}
                helperText={errors.dropInPrice?.message}
                slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
                {...register("dropInPrice")}
              />
            ) : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                id="track-min"
                label="Min students"
                slotProps={{ htmlInput: { min: 0 } }}
                type="number"
                {...register("minStudents")}
              />
              <TextField
                error={Boolean(errors.maxStudents)}
                fullWidth
                helperText={errors.maxStudents?.message}
                id="track-max"
                label="Max students"
                slotProps={{ htmlInput: { min: 1 } }}
                type="number"
                {...register("maxStudents")}
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                id="track-age-min"
                label="Min age (optional)"
                slotProps={{ htmlInput: { min: 0 } }}
                type="number"
                {...register("ageMin")}
              />
              <TextField
                fullWidth
                id="track-age-max"
                label="Max age (optional)"
                slotProps={{ htmlInput: { max: 120, min: 0 } }}
                type="number"
                {...register("ageMax")}
              />
            </Stack>
            <Controller
              control={control}
              name="skillLevel"
              render={({ field }) => (
                <TextField {...field} fullWidth label="Skill level" select>
                  {SKILL_LEVEL_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {fmtLabel(option)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            {courts.length > 0 ? (
              <FormControl component="fieldset">
                <Typography component="legend" variant="subtitle2">
                  Courts (optional)
                </Typography>
                <FormGroup row>
                  {courts.map((court) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedCourtIds.includes(court.id)}
                          onChange={() => {
                            const next = selectedCourtIds.includes(court.id)
                              ? selectedCourtIds.filter(
                                  (courtId) => courtId !== court.id,
                                )
                              : [...selectedCourtIds, court.id];
                            setValue("defaultCourtIds", next, {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                        />
                      }
                      key={court.id}
                      label={court.name}
                    />
                  ))}
                </FormGroup>
                <FormHelperText>
                  These courts are claimed by each published session.
                </FormHelperText>
              </FormControl>
            ) : null}
            {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting} onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            disabled={isSubmitting}
            size="small"
            type="submit"
            variant="contained"
          >
            {isSubmitting ? "Adding..." : "Add track"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function ProgramDetail() {
  const { programId = "" } = useParams<{ programId: string }>();
  const queryClient = useQueryClient();
  const identity = useProgramPreviewIdentity();
  const currentIdentityRef = useRef(identity);
  currentIdentityRef.current = identity;
  const metadataKey = [
    "program-preview",
    identity,
    "business-programs",
    programId,
    "metadata",
  ];
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const currentProgramIdRef = useRef(programId);
  currentProgramIdRef.current = programId;
  const lifecycleActivationRef = useRef({ identity, programId });
  if (
    lifecycleActivationRef.current.identity !== identity ||
    lifecycleActivationRef.current.programId !== programId
  ) {
    // Returning to the same program starts a new activation of its controls.
    lifecycleActivationRef.current = { identity, programId };
  }

  const [confirmPublish, setConfirmPublish] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [addTrackOpen, setAddTrackOpen] = useState(false);
  const [selectedRosterTrackId, setSelectedRosterTrackId] = useState("");
  const currentRosterTrackRef = useRef(selectedRosterTrackId);
  currentRosterTrackRef.current = selectedRosterTrackId;
  const [pendingDeleteTrack, setPendingDeleteTrack] =
    useState<ProgramTrackOptionResponse | null>(null);
  const [lifecyclePending, setLifecyclePending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishRefreshError, setPublishRefreshError] = useState<string | null>(
    null,
  );
  const lifecycleInFlight = useRef<symbol | null>(null);
  const deleteInFlight = useRef(false);

  const programQuery = useProgramPreviewQuery({
    scope: [identity, "business-programs", programId, "metadata"],
    load: () => getProgram(programId),
    enabled: Boolean(programId),
  });
  const program = programQuery.data;
  const facilityId = program?.facility_id ?? null;
  const tracksQuery = useProgramPreviewPage({
    scope: [
      identity,
      "business-programs",
      programId,
      facilityId,
      "authenticated",
      "options",
    ],
    enabled: Boolean(programId) && programQuery.ready,
    load: (cursor, signal) =>
      listTracks(
        programId,
        { limit: 25, ...(cursor ? { cursor } : {}) },
        signal,
      ),
  });
  const tracks = tracksQuery.data?.items ?? [];
  const verifiedEmptyTracks =
    tracksQuery.ready &&
    tracks.length === 0 &&
    !tracksQuery.canPrevious &&
    !tracksQuery.canNext &&
    tracksQuery.data?.has_more === false;
  const rosterTrackQuery = useProgramPreviewQuery({
    scope: [
      identity,
      "business-programs",
      programId,
      facilityId,
      "roster-track",
      selectedRosterTrackId,
    ],
    enabled: programQuery.ready && Boolean(selectedRosterTrackId),
    load: (signal) => getTrack(programId, selectedRosterTrackId, signal),
  });
  useEffect(() => {
    if (
      tracksQuery.ready &&
      selectedRosterTrackId &&
      !tracks.some((track) => track.track_id === selectedRosterTrackId)
    ) {
      setSelectedRosterTrackId("");
    }
  }, [tracks, selectedRosterTrackId, tracksQuery.ready]);

  const courtsQuery = useQuery({
    queryKey: ["programs", "detail", identity, programId, "courts", facilityId],
    queryFn: async (): Promise<CourtOption[]> => {
      if (!facilityId) return [];
      const result = await listCourts(facilityId);
      return result.map((court) => ({ id: court.court_id, name: court.name }));
    },
    enabled: Boolean(facilityId),
    retry: false,
  });
  const courts =
    courtsQuery.isSuccess && courtsQuery.fetchStatus === "idle"
      ? courtsQuery.data
      : [];

  const publishMutation = useMutation({
    mutationFn: (id: string) => publishProgram(id),
  });
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      cancelProgram(id, reason),
  });
  const createTrackMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProgramTrackCreate }) =>
      createTrack(id, data),
  });
  const deleteTrackMutation = useMutation({
    mutationFn: (trackId: string) => deleteTrack(trackId),
  });

  const cancelForm = useForm<CancelProgramValues>({
    defaultValues: { reason: "" },
    resolver: zodResolver(cancelProgramSchema),
  });

  useEffect(() => {
    setConfirmPublish(false);
    setCancelOpen(false);
    setAddTrackOpen(false);
    setSelectedRosterTrackId("");
    setPendingDeleteTrack(null);
    setLifecyclePending(false);
    setActionError(null);
    setPublishRefreshError(null);
    cancelForm.reset({ reason: "" });
    lifecycleInFlight.current = null;
    deleteInFlight.current = false;
    publishMutation.reset();
    cancelMutation.reset();
    createTrackMutation.reset();
    deleteTrackMutation.reset();
    return () => {
      lifecycleInFlight.current = null;
    };
  }, [programId, identity]);

  const refreshTracks = async () => {
    const capturedIdentity = identity;
    const capturedProgram = programId;
    const capturedTrack = selectedRosterTrackId;
    if (
      currentIdentityRef.current !== capturedIdentity ||
      currentProgramIdRef.current !== capturedProgram
    )
      return;
    await Promise.all([
      tracksQuery.refetch(),
      ...(capturedTrack && currentRosterTrackRef.current === capturedTrack
        ? [rosterTrackQuery.refetch()]
        : []),
    ]);
  };

  const handlePublish = async () => {
    if (lifecycleInFlight.current || !programQuery.ready) return;
    const targetProgramId = programId;
    const targetIdentity = identity;
    const activation = lifecycleActivationRef.current;
    const attempt = Symbol();
    lifecycleInFlight.current = attempt;
    const isCurrentAttempt = () =>
      lifecycleActivationRef.current === activation &&
      lifecycleInFlight.current === attempt;
    setLifecyclePending(true);
    setActionError(null);
    let publishSucceeded = false;
    try {
      await publishMutation.mutateAsync(targetProgramId);
      publishSucceeded = true;
      if (currentIdentityRef.current !== targetIdentity) return;
      if (isCurrentAttempt()) {
        setConfirmPublish(false);
      }
      await queryClient.invalidateQueries(
        {
          queryKey: [
            "program-preview",
            identity,
            "business-programs",
            targetProgramId,
          ],
          predicate: (query) =>
            ["sessions", "private-exact-sessions"].includes(
              query.queryKey[6] as string,
            ),
          refetchType: "active",
        },
        { throwOnError: true },
      );
      const authoritative = await getProgram(targetProgramId);
      if (!isCurrentAttempt()) return;
      queryClient.setQueryData(metadataKey, authoritative);
      await queryClient.invalidateQueries({
        queryKey: ["programs", "list"],
        refetchType: "none",
      });
    } catch (error) {
      if (!isCurrentAttempt()) return;
      const message =
        error instanceof Error
          ? error.message
          : publishSucceeded
            ? "This program was published, but we couldn't refresh its current state."
            : "Failed to publish this program.";
      if (publishSucceeded) {
        setPublishRefreshError(message);
      } else {
        setActionError(message);
      }
    } finally {
      if (isCurrentAttempt()) {
        lifecycleInFlight.current = null;
        setLifecyclePending(false);
      }
    }
  };

  const handleCancel = cancelForm.handleSubmit(async ({ reason }) => {
    if (lifecycleInFlight.current || !programQuery.ready) return;
    const targetProgramId = programId;
    const activation = lifecycleActivationRef.current;
    const attempt = Symbol();
    lifecycleInFlight.current = attempt;
    const isCurrentAttempt = () =>
      lifecycleActivationRef.current === activation &&
      lifecycleInFlight.current === attempt;
    setLifecyclePending(true);
    setActionError(null);
    try {
      const updated = await cancelMutation.mutateAsync({
        id: targetProgramId,
        reason: reason.trim() || undefined,
      });
      if (!isCurrentAttempt()) return;
      queryClient.setQueryData(metadataKey, updated);
      await queryClient.invalidateQueries({
        queryKey: ["programs", "list"],
        refetchType: "none",
      });
      if (isCurrentAttempt()) {
        setCancelOpen(false);
        cancelForm.reset({ reason: "" });
      }
    } catch (error) {
      if (isCurrentAttempt()) {
        setActionError(
          error instanceof Error
            ? error.message
            : "Failed to cancel this program.",
        );
      }
    } finally {
      if (isCurrentAttempt()) {
        lifecycleInFlight.current = null;
        setLifecyclePending(false);
      }
    }
  });

  const handleAddTrack = async (data: ProgramTrackCreate) => {
    if (!programQuery.ready) return;
    const targetIdentity = identity;
    const targetProgramId = programId;
    await createTrackMutation.mutateAsync({ id: programId, data });
    if (
      currentIdentityRef.current !== targetIdentity ||
      currentProgramIdRef.current !== targetProgramId
    )
      return;
    setAddTrackOpen(false);
    await refreshTracks();
  };

  const handleDeleteTrack = async () => {
    if (
      deleteInFlight.current ||
      !pendingDeleteTrack ||
      !tracksQuery.ready ||
      !tracks.includes(pendingDeleteTrack)
    )
      return;
    deleteInFlight.current = true;
    const targetIdentity = identity;
    const targetProgramId = programId;
    setActionError(null);
    try {
      await deleteTrackMutation.mutateAsync(pendingDeleteTrack.track_id);
      if (
        currentIdentityRef.current !== targetIdentity ||
        currentProgramIdRef.current !== targetProgramId
      )
        return;
      setPendingDeleteTrack(null);
      await refreshTracks();
    } catch (error) {
      if (
        currentProgramIdRef.current !== targetProgramId ||
        currentIdentityRef.current !== targetIdentity
      )
        return;
      setActionError(
        error instanceof Error ? error.message : "Failed to delete this track.",
      );
    } finally {
      if (
        currentProgramIdRef.current === targetProgramId &&
        currentIdentityRef.current === targetIdentity
      )
        deleteInFlight.current = false;
    }
  };

  const retryLoad = async () => {
    const [programResult, tracksResult] = await Promise.all([
      programQuery.refetch(),
      tracksQuery.refetch(),
    ]);
    if (programResult.isSuccess && tracksResult.isSuccess) {
      setPublishRefreshError(null);
    }
  };

  const loading = programQuery.isLoading;
  const loadError =
    publishRefreshError ??
    (programQuery.data === undefined && programQuery.error instanceof Error
      ? programQuery.error.message
      : null);

  if (loading) {
    return (
      <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 } }}>
        <Skeleton height={48} variant="rounded" width={320} />
        <Paper sx={{ p: 3 }} variant="outlined">
          <Stack spacing={2}>
            <Skeleton height={28} width="35%" />
            <Skeleton height={22} />
            <Skeleton height={22} width="75%" />
          </Stack>
        </Paper>
        <Paper sx={{ p: 3 }} variant="outlined">
          <Skeleton height={120} variant="rounded" />
        </Paper>
      </Stack>
    );
  }

  if (loadError || !program) {
    return (
      <Stack sx={{ px: { xs: 2, lg: 3 } }}>
        <Paper sx={{ p: 4, textAlign: "center" }} variant="outlined">
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <Alert severity="error">{loadError ?? "Program not found."}</Alert>
            <Button onClick={() => void retryLoad()} variant="outlined">
              Try again
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  if (program.audience === "private_coaching") {
    return (
      <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 } }}>
        <ProgramTitle program={program} />
        <Alert severity="info">
          Assigning another coach changes future delivery only. The player’s
          purchase, accepted terms, schedule, and payment history stay unchanged.
        </Alert>
        <Paper component="section" aria-label="Private coaching overview"
          variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Typography component="h2" variant="h6">Overview</Typography>
            <Box sx={{ display: "grid", gap: 2,
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" } }}>
              <FactRow label="Starts">{formatDate(program.start_date, program.timezone)}</FactRow>
              <FactRow label="Ends">{formatDate(program.end_date, program.timezone)}</FactRow>
              <FactRow label="Session length">{program.schedule_duration_minutes} min</FactRow>
              <FactRow label="Timezone">{program.timezone || "—"}</FactRow>
            </Box>
          </Stack>
        </Paper>
        <ProgramInstructorCard program={program} onUpdated={() => programQuery.refetch()} />
      </Stack>
    );
  }

  const status = program.status;
  const canPublish = status === "draft";
  const canCancel = status === "published";

  return (
    <>
      <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <ProgramTitle program={program} />
          {canPublish ? (
            <Button
              disabled={lifecyclePending}
              onClick={() => {
                setActionError(null);
                setConfirmPublish(true);
              }}
              variant="contained"
            >
              Publish
            </Button>
          ) : canCancel ? (
            <Button
              color="error"
              disabled={lifecyclePending}
              onClick={() => {
                setActionError(null);
                cancelForm.reset({ reason: "" });
                setCancelOpen(true);
              }}
              variant="contained"
            >
              Cancel program
            </Button>
          ) : null}
        </Stack>

        {actionError ? <Alert severity="error">{actionError}</Alert> : null}

        <Paper
          aria-labelledby="program-overview-heading"
          component="section"
          sx={{ p: { xs: 2, sm: 3 } }}
          variant="outlined"
        >
          <Stack spacing={2}>
            <Typography
              component="h2"
              id="program-overview-heading"
              variant="h6"
            >
              Overview
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
              }}
            >
              <FactRow label="Description" wide>
                {program.description || "—"}
              </FactRow>
              <FactRow label="Starts">
                {formatDate(program.start_date, program.timezone)}
              </FactRow>
              <FactRow label="Ends">
                {formatDate(program.end_date, program.timezone)}
              </FactRow>
              <FactRow label="Session length">
                {program.schedule_duration_minutes} min
              </FactRow>
              <FactRow label="Timezone" wide>
                {program.timezone || "—"}
              </FactRow>
              <FactRow label="Schedule rules">
                {program.schedule_rrules.length > 0
                  ? `${program.schedule_rrules.length} recurrence rule${program.schedule_rrules.length === 1 ? "" : "s"}`
                  : "—"}
              </FactRow>
              <FactRow label="Active enrollments">
                {program.active_enrollment_count}
              </FactRow>
              <FactRow label="Payee">{fmtLabel(program.payee_type)}</FactRow>
            </Box>
          </Stack>
        </Paper>

        <Paper
          aria-labelledby="program-tracks-heading"
          component="section"
          sx={{ p: { xs: 2, sm: 3 } }}
          variant="outlined"
        >
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography
                component="h2"
                id="program-tracks-heading"
                variant="h6"
              >
                Tracks
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                  alignItems: "center",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  size="small"
                  aria-label="Refresh options"
                  startIcon={<RefreshRounded />}
                  sx={{ minHeight: 44 }}
                  disabled={tracksQuery.isLoading}
                  onClick={tracksQuery.restart}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  sx={{ minHeight: 44 }}
                  onClick={() => setAddTrackOpen(true)}
                  startIcon={<AddRounded />}
                  variant="contained"
                >
                  Add track
                </Button>
              </Stack>
            </Stack>
            {tracksQuery.isError ? (
              <Alert severity="error">Options unavailable right now.</Alert>
            ) : tracksQuery.isLoading ? (
              <Typography role="status">Loading options…</Typography>
            ) : tracks.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 1 }}>
                {verifiedEmptyTracks
                  ? "Add a track to set up sessions and enrollment."
                  : tracksQuery.canNext
                    ? "More options remain"
                    : "No options on this page."}
              </Typography>
            ) : isDesktop ? (
              <TableContainer>
                <Table aria-label="Program tracks" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Fee</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Ages</TableCell>
                      <TableCell>Skill</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tracks.map((track) => {
                      return (
                        <TableRow key={track.track_id}>
                          <TableCell>{track.name}</TableCell>
                          <TableCell>
                            {formatMoney(track.base_price, track.currency)}
                            <Typography
                              color="text.secondary"
                              variant="caption"
                            >
                              {` · ${fmtLabel(track.fee_type)}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {track.enrolled_count}/{track.max_students} enrolled
                            <Typography
                              color="text.secondary"
                              variant="caption"
                            >
                              {` · ${track.available_spots} open${track.waitlist_count > 0 ? ` · ${track.waitlist_count} waitlisted` : ""}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatAgeRange(track.age_min, track.age_max)}
                          </TableCell>
                          <TableCell>{fmtLabel(track.skill_level)}</TableCell>
                          <TableCell>
                            <Chip
                              color={
                                TRACK_STATUS_COLOR[track.status] ?? "default"
                              }
                              label={fmtLabel(track.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={`Delete track ${track.name}`}>
                              <IconButton
                                aria-label={`Delete track ${track.name}`}
                                color="error"
                                onClick={() => setPendingDeleteTrack(track)}
                              >
                                <DeleteOutlineRounded />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <List aria-label="Program tracks" disablePadding>
                {tracks.map((track) => {
                  return (
                    <ListItem
                      divider
                      key={track.track_id}
                      secondaryAction={
                        <Tooltip title={`Delete track ${track.name}`}>
                          <IconButton
                            aria-label={`Delete track ${track.name}`}
                            color="error"
                            edge="end"
                            onClick={() => setPendingDeleteTrack(track)}
                          >
                            <DeleteOutlineRounded />
                          </IconButton>
                        </Tooltip>
                      }
                      sx={{ pl: 0, pr: 7, py: 1.5 }}
                    >
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <Typography
                              sx={{ fontWeight: 600 }}
                              variant="body2"
                            >
                              {track.name}
                            </Typography>
                            <Chip
                              color={
                                TRACK_STATUS_COLOR[track.status] ?? "default"
                              }
                              label={fmtLabel(track.status)}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        }
                        secondary={`${formatMoney(track.base_price, track.currency)} · ${fmtLabel(track.fee_type)} · ${track.enrolled_count}/${track.max_students} enrolled · ${track.available_spots} open · ${formatAgeRange(track.age_min, track.age_max)} · ${fmtLabel(track.skill_level)}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
            {tracksQuery.canPrevious || tracksQuery.canNext ? (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                  flexWrap: "wrap",
                  "& .MuiButton-root": { whiteSpace: "nowrap" },
                }}
              >
                <Button
                  size="small"
                  disabled={!tracksQuery.ready || !tracksQuery.canPrevious}
                  onClick={tracksQuery.previous}
                >
                  Previous options
                </Button>
                <Button
                  size="small"
                  disabled={!tracksQuery.ready || !tracksQuery.canNext}
                  onClick={tracksQuery.next}
                >
                  Next options
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Paper>

        <SessionsSection
          optionsReady={tracksQuery.ready}
          courts={courts}
          key={program.program_id}
          program={program}
          tracks={tracks}
        />

        <ProgramInstructorCard
          program={program}
          onUpdated={() => programQuery.refetch()}
        />

        <ProgramLearningSection programId={program.program_id} />

        <Paper
          aria-labelledby="program-enrollment-heading"
          component="section"
          sx={{ p: { xs: 2, sm: 3 } }}
          variant="outlined"
        >
          <Stack spacing={2}>
            <Typography
              component="h2"
              id="program-enrollment-heading"
              variant="h6"
            >
              Enrollment
            </Typography>
            {verifiedEmptyTracks && !selectedRosterTrackId ? (
              <Typography color="text.secondary">
                Add a track before enrolling learners.
              </Typography>
            ) : (
              <FormControl size="small">
                <InputLabel id="roster-option-label">Roster option</InputLabel>
                <Select
                  labelId="roster-option-label"
                  label="Roster option"
                  value={selectedRosterTrackId}
                  disabled={!tracksQuery.ready}
                  onChange={(event) =>
                    setSelectedRosterTrackId(event.target.value)
                  }
                >
                  <MenuItem value="">Choose an option</MenuItem>
                  {tracks.map((track) => (
                    <MenuItem key={track.track_id} value={track.track_id}>
                      {track.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {selectedRosterTrackId ? (
              rosterTrackQuery.isError ? (
                <Alert severity="error">
                  Couldn&apos;t load this enrollment option.
                </Alert>
              ) : rosterTrackQuery.data ? (
                <RosterSection
                  key={`${identity}:${selectedRosterTrackId}`}
                  onCancelled={refreshTracks}
                  track={rosterTrackQuery.data}
                />
              ) : (
                <Typography role="status">
                  Loading enrollment option…
                </Typography>
              )
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <AddTrackDialog
        courts={courts}
        onClose={() => setAddTrackOpen(false)}
        onSubmit={handleAddTrack}
        open={addTrackOpen}
      />

      <Dialog
        aria-labelledby="publish-program-title"
        fullWidth
        maxWidth="sm"
        onClose={lifecyclePending ? undefined : () => setConfirmPublish(false)}
        open={confirmPublish}
      >
        <DialogTitle id="publish-program-title">
          Publish this program?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Publishing “{program.name}” makes it visible to players for
            registration. You can cancel it later if needed.
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={lifecyclePending}
            onClick={() => setConfirmPublish(false)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            disabled={lifecyclePending}
            onClick={() => void handlePublish()}
            size="small"
            variant="contained"
          >
            {lifecyclePending ? "Publishing..." : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby="cancel-program-title"
        fullWidth
        maxWidth="sm"
        onClose={lifecyclePending ? undefined : () => setCancelOpen(false)}
        open={cancelOpen}
      >
        <Box component="form" onSubmit={handleCancel}>
          <DialogTitle id="cancel-program-title">
            Cancel this program?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Cancelling “{program.name}” stops new registrations. This cannot
              be undone.
            </DialogContentText>
            <TextField
              fullWidth
              id="cancel-reason"
              label="Reason (optional)"
              multiline
              placeholder="Why is this program being cancelled?"
              rows={3}
              {...cancelForm.register("reason")}
            />
            {actionError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {actionError}
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={lifecyclePending}
              onClick={() => setCancelOpen(false)}
              size="small"
            >
              Keep program
            </Button>
            <Button
              color="error"
              disabled={lifecyclePending}
              size="small"
              type="submit"
              variant="contained"
            >
              {lifecyclePending ? "Cancelling..." : "Cancel program"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        aria-labelledby="delete-track-title"
        fullWidth
        maxWidth="sm"
        onClose={
          deleteTrackMutation.isPending
            ? undefined
            : () => setPendingDeleteTrack(null)
        }
        open={pendingDeleteTrack !== null}
      >
        <DialogTitle id="delete-track-title">Delete this track?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeleteTrack
              ? `“${pendingDeleteTrack.name}” will be removed from this program. This cannot be undone.`
              : ""}
          </DialogContentText>
          {actionError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleteTrackMutation.isPending}
            onClick={() => setPendingDeleteTrack(null)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleteTrackMutation.isPending}
            onClick={() => void handleDeleteTrack()}
            size="small"
            variant="contained"
          >
            {deleteTrackMutation.isPending ? "Deleting..." : "Delete track"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
